import React, { useState, useEffect } from 'react';
import { processImage } from '../../utils/imageProcessing';
import { AlertCircle } from 'lucide-react';
import { getImageDimensions } from '../../utils/imageUtils';

interface SizeResizeProps {
  originalImage: File;
  onProcess: (dataUrl: string) => void;
  onProcessingChange: (processing: boolean) => void;
  outputFormat: string;
}

export function SizeResize({
  originalImage,
  onProcess,
  onProcessingChange,
  outputFormat,
}: SizeResizeProps) {
  const originalKb = Math.max(1, Math.round(originalImage.size / 1024));
  const [targetSize, setTargetSize] = useState(() => Math.min(500, Math.max(10, Math.round(originalKb * 0.8))));
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [originalDimensions, setOriginalDimensions] = useState<{width: number, height: number} | null>(null);

  useEffect(() => {
    getImageDimensions(originalImage).then(setOriginalDimensions);
  }, [originalImage]);

  const validateTargetSize = (size: number) => {
    if (size <= 0) {
      return 'Target size must be greater than 0 KB';
    }
    if (size > originalKb) {
      return `Target size cannot exceed original file size (${originalKb} KB)`;
    }
    return null;
  };

  const handleProcess = async () => {
    const validationError = validateTargetSize(targetSize);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);
    setProcessing(true);
    onProcessingChange(true);
    
    try {
      const result = await processImage(originalImage, {
        output: {
          format: outputFormat,
          targetSize,
        }
      });
      onProcess(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process image');
    } finally {
      setProcessing(false);
      onProcessingChange(false);
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
          Target File Size (KB)
        </label>
        <div className="flex rounded-xl shadow-sm border border-slate-300 dark:border-slate-800 overflow-hidden focus-within:ring-2 focus-within:ring-indigo-500 bg-white dark:bg-slate-900">
          <input
            type="number"
            value={targetSize || ''}
            onChange={(e) => {
              const value = Number(e.target.value);
              setTargetSize(value);
              setError(validateTargetSize(value));
            }}
            min="1"
            className="block w-full px-3 py-2 text-slate-900 dark:text-slate-100 bg-transparent border-none focus:outline-none text-xs font-medium"
            placeholder="e.g. 200"
          />
          <span className="inline-flex items-center px-3 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 text-xs font-bold border-l border-slate-200 dark:border-slate-700">
            KB
          </span>
        </div>
        {error && (
          <div className="mt-2 flex items-center gap-1.5 text-xs text-red-500 font-medium">
            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}
        {originalDimensions && (
          <div className="mt-2 text-[11px] text-slate-600 dark:text-slate-400 flex justify-between items-center bg-slate-100 dark:bg-slate-950/80 border border-slate-200/80 dark:border-slate-800 px-3 py-1.5 rounded-lg">
            <span>Original: <strong className="text-slate-800 dark:text-slate-200">{originalKb} KB</strong></span>
            <span className="font-mono">Dimensions: {originalDimensions.width}×{originalDimensions.height}px</span>
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={handleProcess}
        disabled={processing}
        className="w-full py-2.5 px-4 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl shadow-md transition-all hover:scale-[1.01]"
      >
        {processing ? 'Optimizing Image...' : 'Optimize Image Size'}
      </button>
    </div>
  );
}